import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EmptyCard, LoadingBlock, PageHeader } from '@/components/PageHeader';
import { inputStyle } from '@/lib/format';

import { refLabel, useOpenRef, useSafeSearch, type ExploreKind } from './exploreApi';
import { LegacyRecordModal } from './LegacyArchivePage';

const KINDS: Array<{ value: ExploreKind | ''; label: string }> = [
  { value: '', label: '전체' },
  { value: 'JOURNAL', label: '일지' },
  { value: 'VOICE', label: '아차사고 · 의견' },
  { value: 'LEGACY', label: '기존 데이터' },
];

/**
 * 검색(DOC-136 · 사용자 결정 2026-10-28) — 칸 하나로 옛 기록 · 일지 · 아차사고/의견을 함께 찾는다.
 * 전체 화면(/search)은 볼 수 있는 현장 전부, 현장 화면(/sites/:id/search)은 그 현장만. 두 글자부터 찾는다.
 */
export function SafeSearchPage({ siteId }: { siteId?: string }) {
  // 검색어는 머리줄 검색칸이 주소(?q=)에 싣는다 — 이 화면에는 입력칸을 두지 않는다(AppShell.HeaderSearch).
  const [params] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const [kind, setKind] = useState<ExploreKind | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  // 머리줄에서 새 검색어로 오면 첫 쪽부터.
  useEffect(() => setPage(1), [q]);
  const search = useSafeSearch({ q, kind, siteId, from, to, page });
  const { open, peek, closePeek } = useOpenRef();

  const pick = (next: () => void) => {
    next();
    setPage(1);
  };
  const ready = [...q].length !== 1;
  const data = search.data;

  return (
    <div className="space-y-5">
      <style>{STYLES}</style>
      <PageHeader title="검색" />
      <div className="flex flex-wrap items-center gap-2">
        <div className="ss-chips">
          {KINDS.map((k) => (
            <button key={k.value} type="button" className={kind === k.value ? 'on' : ''} onClick={() => pick(() => setKind(k.value))}>
              {k.label}
            </button>
          ))}
        </div>
        <input type="date" className="input" style={{ ...inputStyle, width: '150px' }} value={from} onChange={(e) => pick(() => setFrom(e.target.value))} aria-label="시작일" />
        <span style={{ color: 'var(--text-muted)' }}>~</span>
        <input type="date" className="input" style={{ ...inputStyle, width: '150px' }} value={to} onChange={(e) => pick(() => setTo(e.target.value))} aria-label="종료일" />
      </div>

      {!ready ? null : search.isPending ? (
        <LoadingBlock />
      ) : search.isError ? (
        <LoadingBlock />
      ) : !data || data.rows.length === 0 ? (
        <EmptyCard>찾은 기록이 없습니다.</EmptyCard>
      ) : (
        <div className="card p-0" style={{ overflowX: 'auto', opacity: search.isPlaceholderData ? 0.6 : 1 }}>
          <table className="site-table ss-table">
            <thead>
              <tr>
                <th style={{ width: '104px' }}>일자</th>
                {!siteId && <th style={{ textAlign: 'left', width: '220px' }}>현장</th>}
                <th style={{ width: '150px' }}>메뉴</th>
                <th style={{ textAlign: 'left' }}>내용</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={`${row.kind}:${row.type}:${row.refId}`} onClick={() => open(row, row.siteId)}>
                  <td>{row.day ?? '—'}</td>
                  {!siteId && <td style={{ textAlign: 'left' }}>{row.siteName ?? '—'}</td>}
                  <td>{refLabel(row)}</td>
                  <td style={{ textAlign: 'left' }}>
                    {row.title && <div className="ss-title">{row.title}</div>}
                    {row.snippet && <div className="ss-snippet">{highlight(row.snippet, q)}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-end gap-2 px-4 py-3 text-ui-sm">
            <span style={{ color: 'var(--text-secondary)' }}>{page}쪽</span>
            <button type="button" className="btn-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              이전
            </button>
            <button type="button" className="btn-secondary" disabled={!data.hasMore} onClick={() => setPage(page + 1)}>
              다음
            </button>
          </div>
        </div>
      )}

      {peek && <LegacyRecordModal source={peek.source} code={peek.code} onClose={closePeek} />}
    </div>
  );
}

/** 찾은 말에 형광 — 대소문자 무시. 글자로만 자른다(HTML 로 해석하는 자리 없음). */
function highlight(text: string, q: string): ReactNode {
  const needle = q.trim().toLowerCase();
  if (!needle) return text;
  const lower = text.toLowerCase();
  const out: ReactNode[] = [];
  let i = 0;
  for (let at = lower.indexOf(needle); at >= 0; at = lower.indexOf(needle, i)) {
    if (at > i) out.push(text.slice(i, at));
    out.push(<mark key={at}>{text.slice(at, at + needle.length)}</mark>);
    i = at + needle.length;
  }
  if (i < text.length) out.push(text.slice(i));
  return out;
}

const STYLES = `
.ss-chips { display: inline-flex; flex-wrap: wrap; gap: 4px; padding: 4px; border-radius: 10px; background: var(--bg-hover, #f1f5f9); }
.ss-chips button { padding: 6px 10px; border: none; border-radius: 8px; background: transparent; font-size: 13px; cursor: pointer; }
.ss-chips button.on { background: var(--bg-card, #fff); font-weight: 700; box-shadow: 0 1px 2px rgba(0,0,0,.08); }
.ss-table tbody tr { cursor: pointer; }
.ss-table tbody tr:hover { background: var(--bg-hover, #f8fafc); }
.ss-title { font-weight: 600; max-width: 720px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ss-snippet { font-size: 12px; color: var(--text-secondary); max-width: 720px; }
.ss-snippet mark { background: #fef08a; color: inherit; padding: 0 1px; border-radius: 2px; }
`;
